import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useParams } from 'react-router-dom';

import { validateIban } from 'utils/validation';
import { portfolioSelector, usePortfolioSelector } from 'domain/Portfolio';
import { getClientId } from 'utils';
import { useProductsList } from 'hooks/useProducts';
import { useTranslation } from 'react-i18next';
import { useThreeBProductType } from 'hooks/isThreeBProduct';
import handlerRequestCanceling from 'utils/handlerRequestCanceling';
import HandlerError, { AppError } from 'errors/HandlerError';
import { useWithdrawalPlanBase } from 'widgets/WithdrawalPlan/hooks/useWithdrawalPlanBase';
import { getModel } from 'widgets/WithdrawalPlan/models/getModel';
import { sendModel } from 'widgets/WithdrawalPlan/models/sendModel';
import useLayoutController from 'components/Stepper/RouteBasedStepper/useLayoutController';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useChangePlan } from '../context';

interface StrategyData {
    newModelPortfolio?: { Id?: string };
    strategyId?: string;
    newWithdrawalData?: {
        withdrawalRequest?: unknown;
        paymentInstructionRequest?: unknown;
    };
}

interface ChangePlanContext {
    strategy: {
        data?: StrategyData;
        setData: (data: { newWithdrawalData: unknown }) => void;
    };
    portfolioId?: number | string;
    mainOwnerDetails?: {
        firstName?: string;
        lastName?: string;
    };
}

export const useWithdrawalPlanChangePlan = () => {
    const { dfsClientId } = useParams();
    const contactId = getClientId(dfsClientId);

    const [loading, setIsLoading] = useState(false);

    const {
        i18n: { language },
    } = useTranslation();

    const {
        strategy: { data: strategyData, setData: saveWithdrawalData },
        portfolioId,
        mainOwnerDetails,
    } = useChangePlan() as unknown as ChangePlanContext;

    const { products } = useProductsList(contactId);

    const { dataRaw: portfolio } = usePortfolioSelector(portfolioSelector);

    const selectedProduct = products?.find(
        ({ id }) => portfolio?.Product?.Id === id,
    );

    const productSettings = useMemo(
        () => selectedProduct?.settings,
        [selectedProduct],
    );

    const productExternalId = portfolio?.Product?.ExternalId;
    const {
        isAzp, isPk, isZiv, isZifd, isAzpI, isAzpD,
    } = useThreeBProductType(
        { product: { externalId: productExternalId } },
    );

    const {
        data: lastApplicationByPortfolio,
    } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getLastApplicationByPortfolio',
        payload: {
            portfolioId,
            language,
        },
        enabled: !!portfolioId,
        staleTime: 20 * 60 * 1000, // 20 minutes
        gcTime: 25 * 60 * 1000, // 25 minutes
    });

    const investmentId = useMemo(() => lastApplicationByPortfolio?.data?.investmentApplicationId, [lastApplicationByPortfolio?.data?.investmentApplicationId]);

    const {
        data,
        setData,
        withdrawalPlanData,
        planCalculationLoading,
        errorData,
        trigger,
        setErrorData,
        isRegularTransferPlan,
        setIsRegularTransferPlan,
        memberId,
        option,
        isAtLeastOneProductOpened,
        setLoading,
        isLoading,
        optionWithdrawalPeriodicity,
        isOptionsLoading,
        calculateWithdrawalPlan,
        displayWithdrawalPlanCalculations,
        disableDateHandler,
        setPlanCalculationLoading,
        control,
        handleSubmit,
        register,
    } = useWithdrawalPlanBase({
        isAzp,
        isPk,
        isZiv,
        isZifd,
        isAzpD,
        productSettings,
        modelPortfolioId: strategyData?.newModelPortfolio?.Id ?? '',
        strategyId: strategyData?.strategyId,
        investmentApplicationId: investmentId,
        setPageErrors: () => undefined,
    });

    const {
        data: withdrawalData,
    } = useGetCustomService({
        service: 'planService',
        api: 'getWithdrawalData',
        payload: {
            portfolioId,
            language,
        },
        enabled: !!portfolioId,
        staleTime: 20 * 60 * 1000, // 20 minutes
        gcTime: 25 * 60 * 1000, // 25 minutes
    });

    const currentWithdrawalData = useMemo(() => withdrawalData?.data, [withdrawalData?.data]);

    useEffect(() => {
        const firstName = mainOwnerDetails?.firstName || '';
        const lastName = mainOwnerDetails?.lastName || '';

        const adaptedData = getModel(
            {
                withdrawal: strategyData?.newWithdrawalData?.withdrawalRequest,
                paymentInstruction:
                    strategyData?.newWithdrawalData?.paymentInstructionRequest,
            },
            { firstName, lastName, isAzp },
        );

        setData(adaptedData);
    }, [mainOwnerDetails?.firstName, mainOwnerDetails?.lastName, strategyData?.newWithdrawalData, isAzp]);

    const onContinue = useCallback(
        async (dta: any) => {
            setIsLoading(true);
            try {
                const ibanError = await validateIban(dta?.iban);

                if (ibanError) {
                    setErrorData('iban', {
                        message: ibanError as string,
                    });

                    return;
                }

                saveWithdrawalData({
                    newWithdrawalData: sendModel({
                        data: dta,
                        isAzp,
                        ...(withdrawalPlanData || {}),
                    }),
                });
                setStepComplete(true);
            } catch (err) {
                handlerRequestCanceling(
                    HandlerError({
                        setError: (error: AppError) => setPageErrors([error?.message as string ?? '']),
                        setLoading: (val: boolean) => setLoading?.(val),
                    }),
                )(err as Error);
            }
            setIsLoading(false);
        },
        [
            contactId,
            memberId,
            isAzp,
            withdrawalPlanData,
            isRegularTransferPlan,
        ],
    );

    const { setStepComplete, setPageErrors } = useLayoutController({
        callbackOnContinue:
                handleSubmit && typeof handleSubmit === 'function'
                    ? handleSubmit(onContinue)
                    : onContinue,
        isContinueDisabled: loading,
        isBackDisabled: false,
        isContinueLoading: loading,
    });

    return {
        data,
        withdrawalPlanData,
        currentWithdrawalData,
        planCalculationLoading,
        errorData,
        isRegularTransferPlan,
        optionWithdrawalPeriodicity,
        isLoading,
        isOptionsLoading,
        productExternalId,
        isAzp,
        isPk,
        isZiv,
        isZifd,
        isAzpI,
        isAzpD,
        calculateWithdrawalPlan,
        onContinue,
        displayWithdrawalPlanCalculations,
        disableDateHandler,
        setData,
        setErrorData,
        trigger,
        option,
        isAtLeastOneProductOpened,
        setIsRegularTransferPlan,
        setPlanCalculationLoading,
        control,
        register,
    };
};
